import React, { useEffect, useRef } from 'react';
import Modal, { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { useFeatureGate } from '@atlassian/feature-gates-react';

import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { addBreadcrumb } from '../utilities/analytics/error-reporting';
import { closeAwsCaptchaModal } from './aws-captcha-actions';
import { useAwsWafConfig } from './use-aws-waf-config';
import { useAwsCaptchaSuccessCallbackRef } from './aws-captcha-success-callback-context';
import { AWS_CAPTCHA_CONTAINER_ID } from './aws-captcha.constants';

const AWSWafCaptchaComponent = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const successCallbackRef = useAwsCaptchaSuccessCallbackRef();

  const isFeatureEnabled = useFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);

  const awsWafConfig = useAwsWafConfig();
  const activeConfig = isFeatureEnabled ? awsWafConfig : null;

  useEffect(() => {
    if (!isFeatureEnabled) {
      return;
    }

    if (!activeConfig) {
      console.warn('AWS Captcha config is null — cannot render captcha');
      addBreadcrumb({
        message: 'AWS Captcha config is null — cannot render captcha',
        level: 'warning',
        category: 'aws_captcha',
      });
      return;
    }

    const container = containerRef.current;

    if (!container) {
      console.warn('AWS Captcha container ref is null, has config — cannot render captcha');
      addBreadcrumb({
        message: 'AWS Captcha container ref is null — cannot render captcha',
        level: 'warning',
        category: 'aws_captcha',
      });
      return;
    }

    if (!window.AwsWafCaptcha) {
      console.error('AWS Captcha SDK is not available on window — cannot render captcha');
      addBreadcrumb({
        message:
          'Attempted to render AWS Captcha but AWS Captcha SDK not available on window — cannot render captcha',
        level: 'error',
        category: 'aws_captcha',
      });
      return;
    }

    window.AwsWafCaptcha?.renderCaptcha?.(container, {
      apiKey: activeConfig.apiKey,
      onSuccess: () => {
        const onSuccess = successCallbackRef.current;
        successCallbackRef.current = null;

        dispatch(closeAwsCaptchaModal());

        if (onSuccess) {
          onSuccess();
        } else {
          // The "success" callback is missing, meaning that we aren't able to retry the original request.
          // The user will have to retry manually, which is not ideal. Log a breadcrumb so we can investigate.
          addBreadcrumb({
            message:
              'AWS Captcha puzzle succeeded but no onSuccess callback was registered — modal closed, user must retry manually',
            level: 'warning',
            category: 'aws_captcha',
          });
        }
      },
      onError: (error: unknown) => {
        console.log('AWS Captcha Error: ', error);
        dispatch(closeAwsCaptchaModal());
      },
    });
  }, [dispatch, activeConfig, isFeatureEnabled, successCallbackRef]);

  if (!activeConfig) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      data-testid="aws-captcha-modal-content-idf-testid"
      id={AWS_CAPTCHA_CONTAINER_ID}>
      {/* The Captcha SDK JS will replace this once loaded */}
      <Spinner size="large" />
    </div>
  );
};

/**
 * Conditionally render the AWS Captcha modal depending on:
 * 1. Whether the AWS Captcha feature is enabled
 * 2. Whether the puzzle is currently opened (i.e. the user has been challenged and needs to complete a puzzle to continue)
 */
export const MaybeAWSWafCaptchaModal = () => {
  const isFeatureEnabled = useFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);
  const awsWafConfig = useAwsWafConfig();

  const { isCaptchaPuzzleModalOpen } = useAppSelector(state => state.awsCaptcha);

  if (!isFeatureEnabled || !awsWafConfig) {
    return null;
  }

  if (!isCaptchaPuzzleModalOpen) {
    return null;
  }

  return (
    <ModalTransition>
      <Modal
        width="medium"
        testId="aws-captcha-modal-idf-testid"
        // Prevent user from accidentally closing the modal (with the captcha puzzle in it)
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={false}>
        <ModalBody testId="aws-captcha-modal-body-idf-testid">
          <div data-testid="aws-captcha-modal-container-idf-testid">
            <AWSWafCaptchaComponent />
          </div>
        </ModalBody>
      </Modal>
    </ModalTransition>
  );
};
